<template>
  <!--
    BulkMetadataDialog
    Shared dialog for bulk-editing metaData on catalogues OR videos.
  -->
  <b-modal
    id="bulkMetadataModal"
    ref="bulkModal"
    :title="modalTitle"
    size="lg"
    :hide-footer="stage === 'preparing' || stage === 'processing'"
    :no-close-on-backdrop="true"
    :no-close-on-esc="stage === 'processing'"
    :hide-header-close="stage === 'processing'"
    @hidden="onModalHidden"
  >
    <!-- ───────── Stage 1: Configure ───────── -->
    <div v-if="stage === 'configure'" class="bmd">
      <div class="bmd-section">
        <div class="bmd-section-title">
          {{ mode === 'upsert' ? 'Key/Value Pairs' : 'Keys to Delete' }}
          <span class="bmd-section-meta">
            Up to 3 per run · string values only
          </span>
        </div>
        <div class="bmd-pairs">
          <div
            v-for="(pair, idx) in pairs"
            :key="'pair-' + idx"
            class="bmd-pair-row"
          >
            <input
              type="text"
              class="bmd-input bmd-input-key"
              v-model.trim="pair.key"
              placeholder="Key (e.g. auth)"
              maxlength="120"
            />
            <input
              v-if="mode === 'upsert'"
              type="text"
              class="bmd-input bmd-input-value"
              v-model="pair.value"
              placeholder="Value (string)"
              maxlength="500"
            />
            <button
              type="button"
              class="bmd-pair-remove"
              v-if="pairs.length > 1"
              @click="removePair(idx)"
              title="Remove this row"
            >
              ×
            </button>
          </div>
        </div>
        <button
          type="button"
          class="bmd-add-pair-btn"
          v-if="pairs.length < 3"
          @click="addPair"
        >
          + Add another
        </button>
      </div>

      <div class="bmd-section">
        <div class="bmd-section-title">Scope</div>
        <div class="bmd-scope-options">
          <label class="bmd-radio">
            <input type="radio" v-model="scope" value="all" />
            <span>
              <strong>All {{ recordTypeLabelPlural }}</strong>
              <span class="bmd-radio-meta">
                Applied to every {{ recordTypeLabel }} for this customer
              </span>
            </span>
          </label>
          <label class="bmd-radio">
            <input type="radio" v-model="scope" value="include" />
            <span>
              <strong>Include only selected</strong>
              <span class="bmd-radio-meta">Pick specific records below</span>
            </span>
          </label>
          <label class="bmd-radio">
            <input type="radio" v-model="scope" value="exclude" />
            <span>
              <strong>Exclude selected</strong>
              <span class="bmd-radio-meta">All except the ones below</span>
            </span>
          </label>
        </div>
      </div>

      <div v-if="scope !== 'all'" class="bmd-section bmd-section-list">
        <div class="bmd-section-title">
          Select {{ recordTypeLabelPlural }}
          <span class="bmd-selection-badge">{{ selectionCount }} selected</span>
          <button
            type="button"
            class="bmd-link-btn"
            v-if="selectionCount > 0"
            @click="clearSelection"
          >
            Clear all
          </button>
        </div>

        <div class="bmd-list-controls">
          <input
            type="text"
            class="bmd-input bmd-input-search"
            v-model.trim="listSearchInput"
            @input="onSearchInputDebounced"
            placeholder="Search by name…"
          />
        </div>

        <div class="bmd-list-wrap" v-loading="listLoading">
          <table class="bmd-list-table">
            <thead>
              <tr>
                <th class="bmd-list-th-check">
                  <input
                    type="checkbox"
                    :checked="allCurrentPageSelected"
                    :disabled="listItems.length === 0"
                    @change="toggleSelectCurrentPage($event.target.checked)"
                  />
                </th>
                <th class="bmd-list-th-name">Name</th>
                <th class="bmd-list-th-type" v-if="recordType === 'catalogue'">
                  Type
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!listLoading && listItems.length === 0">
                <td
                  :colspan="recordType === 'catalogue' ? 3 : 2"
                  class="bmd-list-empty"
                >
                  No {{ recordTypeLabelPlural }} found.
                </td>
              </tr>
              <tr
                v-for="row in listItems"
                :key="row.id"
                @click="toggleRow(row)"
                class="bmd-list-row"
                :class="{ 'bmd-list-row-selected': !!selectionMap[row.id] }"
              >
                <td class="bmd-list-td-check">
                  <input
                    type="checkbox"
                    :checked="!!selectionMap[row.id]"
                    @click.stop
                    @change="toggleRow(row)"
                  />
                </td>
                <td>{{ row.displayName }}</td>
                <td v-if="recordType === 'catalogue'">{{ row.type || '—' }}</td>
              </tr>
            </tbody>
          </table>
          <div class="bmd-list-pagination" v-if="listTotal > listPerPage">
            <button
              type="button"
              class="bmd-page-btn"
              :disabled="listPage <= 1 || listLoading"
              @click="goToPage(listPage - 1)"
            >
              ‹ Prev
            </button>
            <span class="bmd-page-info">
              Page {{ listPage }} of {{ totalPages }}
              <span class="bmd-page-total">({{ listTotal }} total)</span>
            </span>
            <button
              type="button"
              class="bmd-page-btn"
              :disabled="listPage >= totalPages || listLoading"
              @click="goToPage(listPage + 1)"
            >
              Next ›
            </button>
          </div>
        </div>
      </div>

      <div v-if="validationErrors.length" class="bmd-errors">
        <strong>Please fix:</strong>
        <ul>
          <li v-for="(e, i) in validationErrors" :key="i">{{ e }}</li>
        </ul>
      </div>
    </div>

    <!-- ───────── Stage 2: Preparing (dry-run API call) ───────── -->
    <div v-else-if="stage === 'preparing'" class="bmd bmd-center">
      <i class="el-icon-loading bmd-big-loading"></i>
      <p class="bmd-big-loading-text">
        Counting affected {{ recordTypeLabelPlural }}…
      </p>
    </div>

    <!-- ───────── Stage 3: Confirm ───────── -->
    <div v-else-if="stage === 'confirm'" class="bmd">
      <div class="bmd-confirm-headline">
        <strong>{{ preview.matched }}</strong>
        {{ preview.matched === 1 ? recordTypeLabel : recordTypeLabelPlural }}
        will be processed.
      </div>

      <div v-if="preview.matched === 0" class="bmd-confirm-empty">
        Nothing to do. The resolved record set is empty.
      </div>

      <div v-else>
        <div class="bmd-confirm-block">
          <div class="bmd-confirm-block-title">Changes per record</div>
          <ul class="bmd-confirm-changes">
            <li v-for="(p, i) in validPairs" :key="'cp-' + i">
              <template v-if="mode === 'upsert'">
                Set
                <code>{{ p.key }}</code>
                =
                <code>"{{ p.value }}"</code>
              </template>
              <template v-else>
                Delete key
                <code>{{ p.key }}</code>
              </template>
            </li>
          </ul>
        </div>

        <div
          class="bmd-confirm-block"
          v-if="preview.sample && preview.sample.length"
        >
          <div class="bmd-confirm-block-title">
            Affected {{ recordTypeLabelPlural }}
            <span class="bmd-affected-meta">
              {{ preview.sample.length }} of {{ preview.matched }}
              <span v-if="preview.sample.length < preview.matched">
                shown · tighten scope to see all
              </span>
            </span>
          </div>
          <ul class="bmd-affected-list">
            <li v-for="s in preview.sample" :key="'ps-' + s.id">
              <a
                :href="editUrlFor(s.id)"
                target="_blank"
                rel="noopener"
                class="bmd-affected-link"
                :title="'Open ' + s.name + ' in a new tab'"
              >
                <span class="bmd-affected-name">{{ s.name }}</span>
                <i class="el-icon-edit-outline bmd-affected-icon"></i>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- ───────── Stage 4: Processing (single API call) ───────── -->
    <div v-else-if="stage === 'processing'" class="bmd bmd-center">
      <i class="el-icon-loading bmd-big-loading"></i>
      <p class="bmd-big-loading-text">
        Updating {{ preview.matched }}
        {{ preview.matched === 1 ? recordTypeLabel : recordTypeLabelPlural }}…
      </p>
    </div>

    <!-- ───────── Stage 5: Result ───────── -->
    <div v-else-if="stage === 'result'" class="bmd">
      <div
        v-if="resultError"
        class="bmd-result-headline bmd-result-headline-warn"
      >
        Bulk update failed.
        <div class="bmd-result-error">{{ resultError }}</div>
      </div>
      <div v-else class="bmd-result-headline" :class="resultHeadlineClass">
        {{ resultHeadlineText }}
      </div>

      <div v-if="!resultError" class="bmd-counts">
        <div class="bmd-count">
          <div class="bmd-count-num bmd-count-succeeded">
            {{ result.modified }}
          </div>
          <div class="bmd-count-label">Modified</div>
        </div>
        <div class="bmd-count">
          <div class="bmd-count-num bmd-count-skipped">
            {{ result.skipped }}
          </div>
          <div class="bmd-count-label">Skipped</div>
        </div>
        <div class="bmd-count">
          <div class="bmd-count-num">{{ result.matched }}</div>
          <div class="bmd-count-label">Matched</div>
        </div>
      </div>

      <div
        v-if="!resultError && result.sample && result.sample.length"
        class="bmd-confirm-block"
      >
        <div class="bmd-confirm-block-title">
          Affected {{ recordTypeLabelPlural }}
          <span class="bmd-affected-meta">
            {{ result.sample.length }} of {{ result.matched }}
            <span v-if="result.sample.length < result.matched">
              shown · tighten scope to see all
            </span>
          </span>
        </div>
        <ul class="bmd-affected-list">
          <li v-for="s in result.sample" :key="'rs-' + s.id">
            <a
              :href="editUrlFor(s.id)"
              target="_blank"
              rel="noopener"
              class="bmd-affected-link"
              :title="'Open ' + s.name + ' in a new tab'"
            >
              <span class="bmd-affected-name">{{ s.name }}</span>
              <i class="el-icon-edit-outline bmd-affected-icon"></i>
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- ───────── Footer (varies per stage) ───────── -->
    <template slot="modal-footer">
      <div v-if="stage === 'configure'" class="bmd-footer">
        <button type="button" class="btn btn-light" @click="close">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="validationErrors.length > 0"
          @click="goToPrepare"
        >
          Next
        </button>
      </div>
      <div v-else-if="stage === 'confirm'" class="bmd-footer">
        <button type="button" class="btn btn-light" @click="back">Back</button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="preview.matched === 0"
          @click="confirm"
        >
          Confirm &amp; Apply
        </button>
      </div>
      <div v-else-if="stage === 'result'" class="bmd-footer">
        <button
          v-if="resultError"
          type="button"
          class="btn btn-warning"
          @click="retryRun"
        >
          Try Again
        </button>
        <button type="button" class="btn btn-primary" @click="close">
          Close
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import bModal from 'bootstrap-vue/es/components/modal/modal'
import customAxios from '../../../service/axios'

// ─── Mongo key restrictions (mirror of server-side validate.js) ───
//
// We enforce these client-side so admins see errors INSTANTLY rather
// than after a round-trip. The server re-checks every constraint, so
// the client validation is purely a UX layer.
//
//   - Empty / whitespace-only keys are obviously wrong.
//   - "$" anywhere is reserved for Mongo operators.
//   - "." is the Mongo path separator (would break $set/$unset paths).
//   - Leading "_" is conventionally reserved (_id, __v, etc.).
function validateMetaKey(key) {
  if (!key || typeof key !== 'string') return 'key is required'
  var k = key.trim()
  if (!k) return 'key is required'
  if (k.length > 120) return 'key is too long (>120 chars)'
  if (k.indexOf('.') !== -1) return "key cannot contain '.'"
  if (k.indexOf('$') !== -1) return "key cannot contain '$'"
  if (k.charAt(0) === '_') return "key cannot start with '_'"
  return null
}

var LIST_PER_PAGE = 10

export default {
  name: 'BulkMetadataDialog',
  components: { bModal },

  props: {
    recordType: {
      type: String,
      required: true,
      validator: function (v) {
        return v === 'catalogue' || v === 'video'
      },
    },
  },

  data: function () {
    return {
      // Lifecycle stage of the modal
      stage: 'configure', // 'configure' | 'preparing' | 'confirm' | 'processing' | 'result'

      // Operation mode (locked when the dialog is opened from a specific
      // dropdown command; admins re-open the dialog to switch modes).
      mode: 'upsert', // 'upsert' | 'delete'

      // Key/value pair rows. For delete mode the value field is ignored.
      pairs: [{ key: '', value: '' }],

      // Scope selection
      scope: 'all', // 'all' | 'include' | 'exclude'

      // Selection state — keyed by record id, Vue-reactive object.
      // Persists across page changes and search refinements.
      selectionMap: {},

      // Listing / search state for include/exclude scopes
      listItems: [],
      listSearchInput: '',
      listSearchActive: '',
      listSearchDebounceTimer: null,
      listPage: 1,
      listPerPage: LIST_PER_PAGE,
      listTotal: 0,
      listLoading: false,

      // Server preview (from the dry-run call). Drives the confirm screen.
      preview: { matched: 0, modified: 0, skipped: 0, sample: [] },

      // Server result (from the real call). Drives the result screen.
      result: { matched: 0, modified: 0, skipped: 0, sample: [] },
      resultError: null,
    }
  },

  computed: {
    recordTypeLabel: function () {
      return this.recordType === 'catalogue' ? 'catalogue' : 'video'
    },
    recordTypeLabelPlural: function () {
      return this.recordType === 'catalogue' ? 'catalogues' : 'videos'
    },
    modalTitle: function () {
      var base =
        this.mode === 'upsert'
          ? 'Bulk Add/Update Metadata'
          : 'Bulk Delete Metadata Keys'
      var stageSuffix = ''
      switch (this.stage) {
        case 'preparing':
          stageSuffix = ' - Preparing'
          break
        case 'confirm':
          stageSuffix = ' - Confirm'
          break
        case 'processing':
          stageSuffix = ' - Processing'
          break
        case 'result':
          stageSuffix = ' - Result'
          break
      }
      return base + stageSuffix
    },
    // Trimmed, validated pair list. Single source of truth used by both
    // validation display and the actual server request payload.
    validPairs: function () {
      return this.pairs
        .map(function (p) {
          return {
            key: (p.key || '').trim(),
            value: p.value == null ? '' : String(p.value),
          }
        })
        .filter(function (p) {
          return p.key.length > 0
        })
    },
    validationErrors: function () {
      var errs = []
      if (this.validPairs.length === 0) {
        errs.push(
          this.mode === 'upsert'
            ? 'Add at least one key/value pair.'
            : 'Add at least one key to delete.',
        )
      }
      var keyErr
      for (var i = 0; i < this.pairs.length; i++) {
        var raw = this.pairs[i].key
        if (raw && raw.trim()) {
          keyErr = validateMetaKey(raw)
          if (keyErr) errs.push('Row ' + (i + 1) + ': ' + keyErr)
        }
      }
      var seen = Object.create(null)
      for (var j = 0; j < this.validPairs.length; j++) {
        var k = this.validPairs[j].key
        if (seen[k]) {
          errs.push(
            'Duplicate key "' +
              k +
              '" — each key can only appear once per run.',
          )
          break
        }
        seen[k] = true
      }
      if (this.scope !== 'all' && this.selectionCount === 0) {
        errs.push(
          this.scope === 'include'
            ? 'Include scope: select at least one ' + this.recordTypeLabel + '.'
            : 'Exclude scope: select at least one ' +
                this.recordTypeLabel +
                ' to exclude.',
        )
      }
      return errs
    },
    selectionCount: function () {
      return Object.keys(this.selectionMap).length
    },
    allCurrentPageSelected: function () {
      if (this.listItems.length === 0) return false
      for (var i = 0; i < this.listItems.length; i++) {
        if (!this.selectionMap[this.listItems[i].id]) return false
      }
      return true
    },
    totalPages: function () {
      if (this.listPerPage <= 0) return 1
      return Math.max(1, Math.ceil(this.listTotal / this.listPerPage))
    },
    resultHeadlineText: function () {
      if (this.result.modified === 0 && this.result.skipped > 0) {
        return 'Nothing changed. All matched records were already in the target state.'
      }
      if (this.result.modified === 0 && this.result.matched === 0) {
        return 'No records matched the scope filter.'
      }
      return (
        'Done! ' +
        this.result.modified +
        ' ' +
        (this.result.modified === 1
          ? this.recordTypeLabel
          : this.recordTypeLabelPlural) +
        ' updated.'
      )
    },
    resultHeadlineClass: function () {
      return 'bmd-result-headline-ok'
    },
  },

  methods: {
    // ─────────────── Public API (called by parent via $refs) ───────────────
    open: function (initialMode) {
      this.mode = initialMode === 'delete' ? 'delete' : 'upsert'
      this.resetAll()
      this.stage = 'configure'
      this.$refs.bulkModal.show()
      // Eager-load first page for the include/exclude selection table.
      this.loadListPage(1)
    },

    resetAll: function () {
      this.pairs = [{ key: '', value: '' }]
      this.scope = 'all'
      this.selectionMap = {}
      this.listItems = []
      this.listSearchInput = ''
      this.listSearchActive = ''
      if (this.listSearchDebounceTimer) {
        clearTimeout(this.listSearchDebounceTimer)
        this.listSearchDebounceTimer = null
      }
      this.listPage = 1
      this.listTotal = 0
      this.preview = { matched: 0, modified: 0, skipped: 0, sample: [] }
      this.result = { matched: 0, modified: 0, skipped: 0, sample: [] }
      this.resultError = null
    },

    // ─────────────── Pair row editing ───────────────
    addPair: function () {
      if (this.pairs.length >= 3) return
      this.pairs.push({ key: '', value: '' })
    },
    removePair: function (idx) {
      if (this.pairs.length <= 1) return
      this.pairs.splice(idx, 1)
    },

    // ─────────────── Selection management ───────────────
    toggleRow: function (row) {
      if (this.selectionMap[row.id]) {
        this.$delete(this.selectionMap, row.id)
      } else {
        this.$set(this.selectionMap, row.id, {
          id: row.id,
          displayName: row.displayName,
          type: row.type || null,
        })
      }
    },
    toggleSelectCurrentPage: function (checked) {
      var self = this
      this.listItems.forEach(function (row) {
        if (checked && !self.selectionMap[row.id]) {
          self.$set(self.selectionMap, row.id, {
            id: row.id,
            displayName: row.displayName,
            type: row.type || null,
          })
        } else if (!checked && self.selectionMap[row.id]) {
          self.$delete(self.selectionMap, row.id)
        }
      })
    },
    clearSelection: function () {
      this.selectionMap = {}
    },

    // ─────────────── Listing + search ───────────────
    onSearchInputDebounced: function () {
      var self = this
      if (this.listSearchDebounceTimer)
        clearTimeout(this.listSearchDebounceTimer)
      this.listSearchDebounceTimer = setTimeout(function () {
        self.listSearchActive = self.listSearchInput
        self.listPage = 1
        self.loadListPage(1)
      }, 300)
    },
    goToPage: function (page) {
      if (page < 1 || page > this.totalPages) return
      this.listPage = page
      this.loadListPage(page)
    },
    // We bypass Vuex for the listing reads because the listing actions
    // mutate app-wide state (the listing page behind the modal). Direct
    // axios calls keep the modal isolated.
    loadListPage: function (page) {
      var self = this
      var customerId = this.$store.state.customerId
      var url
      if (this.recordType === 'catalogue') {
        url = '/api/v1/catalogues?page=' + page + '&limit=' + this.listPerPage
      } else {
        url = '/api/v1/videos?page=' + page + '&limit=' + this.listPerPage
      }
      if (this.listSearchActive) {
        url += '&key=' + encodeURIComponent(this.listSearchActive)
      }
      this.listLoading = true
      customAxios({
        method: 'get',
        url: url,
        headers: { CUSTOMERID: customerId },
      })
        .then(function (response) {
          var data = response.data || {}
          var rawList =
            self.recordType === 'catalogue'
              ? data._list || []
              : data.videosList || []
          self.listItems = rawList.map(function (r) {
            return {
              id: r.id || r._id,
              displayName: self.displayNameOf(r),
              type: r.type || null,
              raw: r,
            }
          })
          self.listTotal = data.count || 0
          self.listLoading = false
        })
        .catch(function (err) {
          self.listLoading = false
          self.$notify({
            title: 'Error',
            message:
              'Could not load ' +
              self.recordTypeLabelPlural +
              ' list: ' +
              (err && err.message ? err.message : 'network error'),
            type: 'warning',
          })
        })
    },

    displayNameOf: function (record) {
      if (!record) return '(unknown)'
      if (record.name) return record.name
      if (
        record.languages &&
        record.languages.length &&
        record.languages[0].title
      ) {
        return record.languages[0].title
      }
      return record.id || record._id || '(unnamed)'
    },

    // Build the edit-page URL for a given record. Used by the affected
    // list links on the Confirm and Result stages. Links open in a new
    // tab (target="_blank" on the anchor) so the admin doesn't lose the
    // dialog state when inspecting a record.
    editUrlFor: function (id) {
      return this.recordType === 'catalogue'
        ? '/#/catalogue/edit/' + id
        : '/#/videos/edit/' + id
    },

    // ─────────────── Stage transitions ───────────────
    goToPrepare: function () {
      if (this.validationErrors.length) return
      this.stage = 'preparing'
      var self = this
      this.callBulkEndpoint(true)
        .then(function (data) {
          // dry-run response shape: { matched, modified:0, skipped:0, sample, dryRun:true }
          self.preview = {
            matched: data.matched || 0,
            modified: data.modified || 0,
            skipped: data.skipped || 0,
            sample: Array.isArray(data.sample) ? data.sample : [],
          }
          self.stage = 'confirm'
        })
        .catch(function (err) {
          self.$notify({
            title: 'Error',
            message: self.errorMessageFrom(
              err,
              'Could not preview bulk update',
            ),
            type: 'warning',
          })
          self.stage = 'configure'
        })
    },

    back: function () {
      this.stage = 'configure'
    },

    confirm: function () {
      if (this.preview.matched === 0) return
      this.stage = 'processing'
      this.resultError = null
      var self = this
      this.callBulkEndpoint(false)
        .then(function (data) {
          self.result = {
            matched: data.matched || 0,
            modified: data.modified || 0,
            skipped: data.skipped || 0,
            sample: Array.isArray(data.sample) ? data.sample : [],
          }
          self.stage = 'result'
        })
        .catch(function (err) {
          self.resultError = self.errorMessageFrom(err, 'Bulk update failed')
          self.stage = 'result'
        })
    },

    retryRun: function () {
      // Re-issue the live (non-dry) call. preview is still populated, so
      // we can use its matched count to gate the action.
      if (this.preview.matched === 0) return
      this.resultError = null
      this.confirm()
    },

    close: function () {
      this.$refs.bulkModal.hide()
    },

    onModalHidden: function () {
      // Emit applied event so the parent page refreshes its listing.
      // We only emit when a real (non-dry) call actually completed.
      if (
        this.stage === 'result' &&
        !this.resultError &&
        this.result.modified > 0
      ) {
        this.$emit('applied', {
          mode: this.mode,
          matched: this.result.matched,
          modified: this.result.modified,
          skipped: this.result.skipped,
        })
      }
    },

    // ─────────────── Server call ───────────────
    //
    // Builds the request payload and dispatches the appropriate Vuex
    // action. Same payload shape for both dry-run and real calls; only
    // the dryRun flag differs.
    callBulkEndpoint: function (dryRun) {
      var payload = {
        mode: this.mode,
        pairs:
          this.mode === 'upsert'
            ? this.validPairs // {key, value} pairs
            : this.validPairs.map(function (p) {
                return { key: p.key } // value omitted for delete mode
              }),
        scope: this.scope,
        dryRun: !!dryRun,
      }
      if (this.scope !== 'all') {
        payload.ids = Object.keys(this.selectionMap)
      }
      var action =
        this.recordType === 'catalogue'
          ? 'bulkUpdateCatalogueMetadata'
          : 'bulkUpdateVideoMetadata'
      return this.$store.dispatch(action, payload)
    },

    errorMessageFrom: function (err, fallback) {
      if (!err) return fallback
      // axios error with server-supplied message
      if (err.response && err.response.data && err.response.data.message) {
        return err.response.data.message
      }
      if (err.message) return err.message
      return fallback
    },
  },
}
</script>

<style scoped>
/* ─────────── Layout ─────────── */
.bmd {
  font-size: 13px;
  color: #27282a;
}
.bmd-center {
  text-align: center;
  padding: 36px 12px;
}

/* ─────────── Sections ─────────── */
.bmd-section {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 14px;
}
.bmd-section-title {
  font-weight: 600;
  font-size: 13px;
  color: #27282a;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.bmd-section-meta {
  font-weight: 400;
  font-size: 11px;
  color: #6c757d;
}
.bmd-section-list {
  padding-bottom: 4px;
}

/* ─────────── Pairs ─────────── */
.bmd-pairs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.bmd-pair-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.bmd-input {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 7px 10px;
  font-size: 13px;
  background: #ffffff;
  flex: 1 1 auto;
  min-width: 0;
}
.bmd-input:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.18);
}
.bmd-input-key {
  flex: 0 0 35%;
}
.bmd-input-value {
  flex: 1 1 auto;
}
.bmd-pair-remove {
  width: 28px;
  height: 28px;
  border: 1px solid #ced4da;
  background: #ffffff;
  border-radius: 4px;
  color: #6c757d;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  flex: 0 0 28px;
}
.bmd-pair-remove:hover {
  background: #f8f9fa;
  color: #dc3545;
}
.bmd-add-pair-btn {
  background: none;
  border: 1px dashed #ced4da;
  color: #6c757d;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-top: 8px;
}
.bmd-add-pair-btn:hover {
  background: #ffffff;
  border-color: #80bdff;
  color: #27282a;
}

/* ─────────── Scope radios ─────────── */
.bmd-scope-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bmd-radio {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-weight: 400;
  margin: 0;
}
.bmd-radio input[type='radio'] {
  margin-top: 3px;
  flex: 0 0 auto;
}
.bmd-radio-meta {
  display: block;
  font-size: 11px;
  color: #6c757d;
  font-weight: 400;
  margin-top: 1px;
}

/* ─────────── Selection table ─────────── */
.bmd-selection-badge {
  background: #e7f1ff;
  color: #0056b3;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}
.bmd-link-btn {
  background: none;
  border: none;
  color: #007bff;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
}
.bmd-link-btn:hover {
  text-decoration: underline;
}

.bmd-list-controls {
  margin-bottom: 8px;
}
.bmd-input-search {
  width: 100%;
}
.bmd-list-wrap {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}
.bmd-list-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.bmd-list-table thead th {
  background: #f1f3f5;
  font-weight: 600;
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #495057;
}
.bmd-list-th-check {
  width: 36px;
}
.bmd-list-th-type {
  width: 120px;
}
.bmd-list-row {
  cursor: pointer;
  transition: background 0.08s;
}
.bmd-list-row:hover {
  background: #f8f9fa;
}
.bmd-list-row-selected {
  background: #e7f1ff;
}
.bmd-list-row-selected:hover {
  background: #d4e6fb;
}
.bmd-list-table tbody td {
  padding: 7px 10px;
  border-bottom: 1px solid #f1f3f5;
}
.bmd-list-td-check {
  text-align: center;
}
.bmd-list-empty {
  text-align: center;
  color: #6c757d;
  padding: 18px;
  font-style: italic;
}

.bmd-list-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
  font-size: 12px;
}
.bmd-page-btn {
  border: 1px solid #ced4da;
  background: #ffffff;
  padding: 4px 10px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
}
.bmd-page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.bmd-page-info {
  color: #495057;
}
.bmd-page-total {
  color: #6c757d;
}

/* ─────────── Validation errors ─────────── */
.bmd-errors {
  background: #fff5f5;
  border: 1px solid #f5c2c7;
  color: #842029;
  padding: 10px 14px;
  border-radius: 4px;
  font-size: 12px;
}
.bmd-errors ul {
  margin: 6px 0 0 16px;
  padding: 0;
}
.bmd-errors li {
  margin-bottom: 2px;
}

/* ─────────── Preparing + Processing center stages ─────────── */
.bmd-big-loading {
  font-size: 36px;
  color: #6c757d;
}
.bmd-big-loading-text {
  margin-top: 14px;
  font-weight: 600;
  font-size: 14px;
  color: #27282a;
}

/* ─────────── Confirm ─────────── */
.bmd-confirm-headline {
  background: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  padding: 12px 16px;
  border-radius: 4px;
  font-size: 15px;
  margin-bottom: 14px;
}
.bmd-confirm-empty {
  color: #6c757d;
  text-align: center;
  padding: 24px;
  font-style: italic;
}
.bmd-confirm-block {
  margin-bottom: 14px;
}
.bmd-confirm-block-title {
  font-weight: 600;
  font-size: 12px;
  color: #495057;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.bmd-confirm-changes,
.bmd-confirm-sample-list {
  list-style: none;
  padding: 0;
  margin: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.bmd-confirm-changes li,
.bmd-confirm-sample-list li {
  padding: 6px 12px;
  border-bottom: 1px solid #f1f3f5;
}
.bmd-confirm-changes li:last-child,
.bmd-confirm-sample-list li:last-child {
  border-bottom: none;
}
.bmd-confirm-changes code,
.bmd-confirm-sample-list code {
  background: #f1f3f5;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 12px;
}

/* ─── Affected list (scrollable, clickable rows) ─── */
.bmd-affected-meta {
  font-weight: 400;
  font-size: 11px;
  color: #6c757d;
  margin-left: 8px;
  text-transform: none;
  letter-spacing: 0;
}
.bmd-affected-list {
  list-style: none;
  padding: 0;
  margin: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  max-height: 320px;
  overflow-y: auto;
}
.bmd-affected-list li {
  border-bottom: 1px solid #f1f3f5;
}
.bmd-affected-list li:last-child {
  border-bottom: none;
}
.bmd-affected-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  color: #27282a;
  text-decoration: none;
  font-size: 13px;
  transition: background 0.08s, color 0.08s;
}
.bmd-affected-link:hover {
  background: #e7f1ff;
  color: #0056b3;
  text-decoration: none;
}
.bmd-affected-name {
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
}
.bmd-affected-icon {
  flex: 0 0 auto;
  color: #adb5bd;
  font-size: 14px;
}
.bmd-affected-link:hover .bmd-affected-icon {
  color: #0056b3;
}

/* ─────────── Counters (shared, used on Result) ─────────── */
.bmd-counts {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-bottom: 14px;
}
.bmd-count {
  text-align: center;
}
.bmd-count-num {
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}
.bmd-count-succeeded {
  color: #28a745;
}
.bmd-count-failed {
  color: #dc3545;
}
.bmd-count-skipped {
  color: #6c757d;
}
.bmd-count-label {
  font-size: 11px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 4px;
}

/* ─────────── Result ─────────── */
.bmd-result-headline {
  padding: 12px 16px;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 14px;
  text-align: center;
}
.bmd-result-headline-ok {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}
.bmd-result-headline-warn {
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  color: #842029;
}
.bmd-result-error {
  font-weight: 400;
  font-size: 13px;
  margin-top: 6px;
}

/* ─────────── Footer ─────────── */
.bmd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
}
</style>
